import type {
  LenderDebitData,
  LenderDelegationEntry,
  DebitData,
} from '@1delta/calldata-sdk'
import {
  FLUID_VAULT_FACTORY,
  getAaveCollateralTokenAddress,
  getAaveStyleLenderTokenAddress,
  getCompoundV2Comptroller,
  getCompoundV3CometAddress,
  getMorphoAddress,
  getPermit2ContractAddress,
  LendingMode,
} from '@1delta/calldata-sdk'
import {
  isCompoundV2Type,
  isCompoundV3,
  isMorphoType,
  isInit,
  isVenusType,
} from '../../../utils'
import { getCompoundV2CollateralTokens } from '../../public-data/addresses/compoundV2'
import {
  isAaveType,
  isAaveV4Type,
  isCompoundV3Type,
  isFluid,
  isLista,
  isSiloV2Type,
  isSiloV3Type,
  Lender,
} from '@1delta/lender-registry'
import { getSiloHalfForUnderlying } from '@1delta/calldata-sdk'

/**
 * Converts an underlying amount to Compound V2 share units,
 * adding a 0.1% buffer to account for rounding.
 */
function toCompoundV2Shares(
  entry: LenderDelegationEntry,
  amount: bigint,
): bigint {
  const raw = entry.params?.exchangeRateCurrent
  if (!raw || raw === '0x') return amount // no rate data → assume 1:1 (conservative)
  try {
    const exRate = BigInt(raw)
    if (exRate === 0n) return amount
    return (((amount * 10n ** 18n) / exRate) * 1001n) / 1000n
  } catch {
    return amount // unparseable → assume 1:1
  }
}

/**
 * Converts an underlying asset amount to silo collateral shares,
 * adding a 0.1% buffer to account for rounding and rate latency.
 *
 * The collateralRate is `convertToAssets(10^decimals, COLLATERAL)`:
 * how many asset units 10^decimals shares are worth.
 *
 *   shares = (assetAmount * 10^decimals) / collateralRate
 */
function toSiloShares(
  entry: LenderDelegationEntry,
  amount: bigint,
  chainId: string,
  lender: string,
  tokenAddress: string,
): bigint {
  const raw = entry.params?.collateralRate
  if (!raw || raw === '0x') return amount // no rate → assume 1:1 (conservative)
  try {
    const rate = BigInt(raw)
    if (rate === 0n) return amount
    // Resolve decimals from the silo half metadata
    const half = getSiloHalfForUnderlying(chainId, lender, tokenAddress)
    if (!half) return amount
    const oneUnit = BigInt(10) ** BigInt(half.decimals)
    return (((amount * oneUnit) / rate) * 1001n) / 1000n
  } catch {
    return amount // unparseable → assume 1:1
  }
}

/**
 * Resolves the key to look up in `LenderDebitData` for a given lender and token.
 * For Compound V2 / Venus types this is the cToken address;
 * for all other lenders it is the tokenAddress as-is.
 */
function resolveDebitDataKey(
  chainId: string,
  lender: string,
  tokenAddress: string,
  cToken?: string,
  isProtected?: boolean,
  isBorrow?: boolean,
  mode?: LendingMode,
): string {
  // Venus BORROW delegation is `comptroller.updateDelegate` — a boolean grant
  // keyed by the COMPTROLLER in the permission read, not by any vToken. The
  // vToken key below is the WITHDRAW side (share allowance); using it for a
  // borrow check compared the debt asset's withdraw allowance (always 0 for a
  // borrow-only grant) and re-emitted the grant forever — the same defect the
  // Silo borrow checks had.
  if (isVenusType(lender) && isBorrow) {
    const comptroller = getCompoundV2Comptroller(chainId, lender)
    if (comptroller) return comptroller.toLowerCase()
  }

  if (isCompoundV2Type(lender) || isVenusType(lender)) {
    if (cToken) return cToken.toLowerCase()
    const tokenMap = getCompoundV2CollateralTokens(chainId, lender)
    if (tokenMap) {
      const resolved = tokenMap[tokenAddress.toLowerCase()]
      if (resolved) return resolved.toLowerCase()
    }
  }

  if (isCompoundV3Type(lender)) {
    const comet = getCompoundV3CometAddress(chainId, lender)
    if (comet) return comet
  }

  if (isMorphoType(lender)) {
    const comet = getMorphoAddress(
      chainId,
      isLista(lender) ? Lender.LISTA_DAO : Lender.MORPHO_BLUE,
    )
    if (comet) return comet
  }

  // Fluid's single grant is `setApprovalForAll` on the global VaultFactory —
  // the permission read stores it under the FACTORY address. Falling through
  // to the raw token key found no entry and reported "approval needed"
  // unconditionally.
  if (isFluid(lender)) {
    return FLUID_VAULT_FACTORY.toLowerCase()
  }

  if (isAaveType(lender)) {
    // The BORROW grant is `approveDelegation` on the DEBT token
    // (variable/stable), and the permission read stores `borrowAllowance`
    // under that debt token's address. The aToken key below is the WITHDRAW
    // side (aToken ERC-20 allowance); using it for a borrow check compared
    // the wrong allowance and re-emitted the credit delegation forever.
    if (isBorrow) {
      try {
        const debtToken = getAaveStyleLenderTokenAddress(
          chainId,
          tokenAddress,
          lender,
          mode ?? LendingMode.VARIABLE,
        )
        if (debtToken) return debtToken.toLowerCase()
      } catch {
        // Unknown reserve → fall through to the aToken/raw key; a missing
        // entry surfaces the permission, which is the safe direction.
      }
    }
    try {
      const aToken = getAaveCollateralTokenAddress(chainId, lender, tokenAddress)
      if (aToken) return aToken
    } catch {
      // Unknown reserve — fall through to the raw token key (fail-safe:
      // no entry ⇒ permission surfaced). The getter THROWS rather than
      // returning undefined, and an uncaught throw here took down the whole
      // response instead of one permission check.
    }
  }

  if (isSiloV2Type(lender) || isSiloV3Type(lender)) {
    const half = getSiloHalfForUnderlying(chainId, lender, tokenAddress)
    if (half) {
      // A BORROW delegation lives on the DEBT share token — keying it to the
      // collateral share compared the check against an allowance the borrow
      // never spends (always 0), so the grant was re-emitted forever.
      if (isBorrow) return half.debtShareToken.toLowerCase()
      return isProtected
        ? half.protectedShareToken.toLowerCase()
        : half.collateralShareToken.toLowerCase()
    }
  }

  return tokenAddress.toLowerCase()
}

/**
 * Checks whether a lender-level delegation/approval is needed for
 * a given token and amount.
 *
 * Returns `true` if approval IS needed, `false` if already approved.
 */
export function needsLenderApproval(params: {
  lender: string
  lenderDebitData: LenderDebitData | undefined
  tokenAddress: string
  amount: bigint
  chainId: string
  cToken?: string
  aaveV4Spoke?: string
  /** Silo: check the protected share token instead of collateral */
  isProtected?: boolean
  /**
   * This is a BORROW delegation check — resolve against the entry the borrow
   * grant actually lives on (Silo: debt share token; Aave: variable/stable
   * debt token's `borrowAllowance`; Venus: the comptroller's `updateDelegate`
   * boolean) instead of the withdraw-side key. Without it a borrow check
   * reads a withdraw allowance that is 0 for a borrow-only grant and the
   * permission is re-emitted forever.
   */
  isBorrow?: boolean
  /** Aave borrow checks: which debt token to resolve (default VARIABLE). */
  mode?: LendingMode
}): boolean {
  const { lender, lenderDebitData, tokenAddress, amount, chainId, cToken, isProtected, isBorrow, mode } =
    params

  if (!lenderDebitData) return true

  // boolean isPositionManager(user, gateway) stored under spoke key
  if (isAaveV4Type(lender)) {
    const key = (params.aaveV4Spoke ?? tokenAddress).toLowerCase()
    const entry = lenderDebitData[key]
    if (!entry || entry.amount === undefined) return true
    return entry.amount === 0n
  }

  // Init / Morpho / Comet: permission data is keyed by authorized address (not underlying token)
  if (isInit(lender) || isMorphoType(lender) || isCompoundV3(lender)) {
    const entry = Object.values(lenderDebitData)[0]
    if (!entry || entry.amount === undefined) return true
    return entry.amount === 0n
  }

  const key = resolveDebitDataKey(chainId, lender, tokenAddress, cToken, isProtected, isBorrow, mode)
  const entry = lenderDebitData[key]
  if (!entry || entry.amount === undefined) return true

  // Venus BORROW: `updateDelegate` is boolean (parse stores maxUint/0 under
  // the comptroller key) — never a share-converted amount comparison.
  if (isVenusType(lender) && isBorrow) {
    return entry.amount === 0n
  }

  // Fluid: `setApprovalForAll` on the VaultFactory — boolean.
  if (isFluid(lender)) {
    return entry.amount === 0n
  }

  // Compound V2 / Venus: allowance is in shares, convert underlying amount
  if (isCompoundV2Type(lender) || isVenusType(lender)) {
    const requiredShares = toCompoundV2Shares(entry, amount)
    return entry.amount < requiredShares
  }

  // Silo V2/V3: allowance is in shares, convert underlying amount
  if (isSiloV2Type(lender) || isSiloV3Type(lender)) {
    const requiredShares = toSiloShares(entry, amount, chainId, lender, tokenAddress)
    return entry.amount < requiredShares
  }

  // Compound V3: boolean-style (maxUint256 = approved, 0 = not)
  if (isCompoundV3(lender)) {
    return entry.amount === 0n
  }

  // Aave types: numeric allowance comparison
  return entry.amount < amount
}

/**
 * Checks whether an ERC20 or Permit2 token approval is needed for
 * a given spender and amount.
 *
 * Returns `true` if approval IS needed, `false` if already approved.
 */
export function needsTokenApproval(params: {
  debitData: DebitData | undefined
  spender: string
  amount: bigint
  chainId: string
  usePermit2?: boolean
}): boolean {
  const { debitData, spender, amount, chainId, usePermit2 } = params

  if (!debitData) return true

  const spenderLower = spender.toLowerCase()

  // Permit2-specific check
  if (usePermit2) {
    const p2 = debitData.permit2?.[spenderLower]
    return !p2 || p2.amount < amount
  }

  // Check if spender is the Permit2 contract — if so, check permit2 allowance
  const permit2Address = getPermit2ContractAddress(chainId).toLowerCase()
  if (spenderLower === permit2Address) {
    const p2 = debitData.permit2?.[spenderLower]
    if (p2 && p2.amount >= amount) return false
  }

  // Standard ERC20 allowance check
  const allowance = debitData.allowances?.[spenderLower] ?? 0n
  return allowance < amount
}
